import type { CSSProperties, FC } from 'react';

/**
 * The mark alone. Sizes hold the art's own 2.69:1 ratio on whole pixels.
 *
 * They were first picked against a BY DIGITALZONE byline that sat under the mark.
 * The byline is gone, but the sizes stay: they are what every surface is already
 * spaced for, and the art box still carries the tail of the "y", so the drawn
 * mark sits higher in its box than its height suggests.
 */
const SIZES = {
  sm: { width: 70, height: 26 },
  md: { width: 80, height: 30 },
  lg: { width: 108, height: 40 },
} as const;

type ThirdEyeLogoSize = keyof typeof SIZES;

/** Which surface the mark sits on. These two tones are the only two we ship. */
type ThirdEyeLogoTone = 'dark' | 'light';

// Tokens, not literals — var() resolves inside an SVG `fill` presentation
// attribute. The retired parchment/plum palette drifted precisely because
// colours were written as hex in TSX and so survived the token rename.
const MARK_FILL: Record<ThirdEyeLogoTone, string> = {
  dark: 'var(--dz-charcoal)',
  light: 'var(--dz-on-charcoal)',
};

interface ThirdEyeLogoProps {
  size?: ThirdEyeLogoSize;
  tone?: ThirdEyeLogoTone;
  className?: string;
  style?: CSSProperties;
}

/**
 * The mark, alone.
 *
 * It carried a BY DIGITALZONE byline until 2026-09-18. That was dropped by
 * request, on every surface at once — app header, the 5 auth screens, onboarding
 * and the email templates. The company name still appears where it has to: the
 * email footers carry it, and the auth and onboarding copy names it.
 */
export const ThirdEyeLogo: FC<ThirdEyeLogoProps> = ({
  size = 'md',
  tone = 'dark',
  className,
  style,
}) => {
  const { width, height } = SIZES[size];
  return (
    <svg
      width={width}
      height={height}
      viewBox='0 0 550 206'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-label='3rd Eye logo'
      role='img'
      className={className}
      style={style}
    >
      {/* Paths copied verbatim from src/assets/logos/thirdeye-black.svg — the
            designer's art, not redrawn. The three near-twin glyphs are theirs too:
            dropping them shifts 2% of the ink. */}
      <g fill={MARK_FILL[tone]}>
        <path d='M51.79,92.8c-1.49-9.01-8.68-12.45-17.18-14.15-2.71-.53-5.76-1.53-7.41-3.92-2.69-3.94-1.58-9.86,2.19-12.7,1.97-1.49,4.35-1.87,6.71-2.28,4.69-.81,9-3,11.35-7.23,2.44-4.4,2.59-9.8.67-14.41-2.15-5.18-7.06-8.24-12.63-8.37-5.06-.34-9.4,1.85-12.24,5.94-1.94,2.86-3.13,5.99-3.67,9.59H1.24c-.27-11.73,5.36-22.48,15.61-28.39,17.8-10.27,40.78-3.24,48.75,15.56,1.81,4.28,2.36,8.57,2.39,13.19.06,9.2-4.32,17.62-12.35,22.73,16.41,9.71,18.91,30.26,10.69,46.4-3.85,7.13-9.82,12.14-17.59,14.65-7.6,2.19-15.54,2.5-23.18.52C9.91,125.67-.39,111.63.01,95.71h18.54c-.07,6.16,2.67,11.85,7.68,15.47,5.71,3.41,12.84,3.45,18.61.16,5.7-4.41,8.13-11.38,6.94-18.54h0Z' />
        <path d='M106.46,74.93l-.14,33.42c-.04,10.24-8.44,18.52-18.78,18.51l.18-53.41c.07-19.24,15.79-30.99,36.34-29.26l-.07,17.66c-10.22-.59-17.51,4.67-17.54,13.07h0Z' />
        <path d='M217.9,89.41c0,21.09-14.02,37.01-34.21,40.98-19.48,3.83-38.2-5.47-47.1-21.76-9.4-17.25-5.3-38.03,8.38-50.51,15.07-13.75,36.59-15.56,53.99-4.31l.02-35c0-10.37,8.48-18.78,18.96-18.81l-.04,89.41ZM199.37,88.84c0-13.43-11.04-24.32-24.66-24.32s-24.66,10.89-24.66,24.32,11.04,24.32,24.66,24.32,24.66-10.89,24.66-24.32Z' />
        <path d='M326.58,95.08h42.6s4.09-21.83-11.95-38.17c-8.77-8.93-19.54-12.89-32.1-12.5-10.43.33-20.45,4.46-28.02,11.58-9.14,8.61-13.6,18.89-13.6,31.06s4.15,21.92,12.65,30.33c8.5,8.22,18.58,12.33,30.64,12.33s21.94-4.11,30.44-12.33c4.04-4,7.06-8.37,9.16-13.12h-21.47c-.19.2-.36.41-.55.6-4.64,4.6-10.49,6.99-17.54,6.99s-12.9-2.4-17.74-6.99c-4.64-4.79-7.06-10.79-7.06-18.18s2.42-13.38,7.06-17.98c4.84-4.79,10.69-7.19,17.74-7.19s12.9,2.4,17.54,7.19c2.45,2.32,4.31,5.8,5.54,8.98h-6.17c-9.48,0-17.17,7.61-17.17,17v.37h-.01Z' />
        <path d='M455.17,47.19v47.99c0,14.18-3.78,24.63-11.13,31.51-7.56,6.88-16.58,10.43-26.88,10.43s-19.32-3.55-26.88-10.43c-7.56-6.88-11.34-17.53-11.34-31.51v-28.58c0-10.72,8.77-19.42,19.61-19.42v47.99c0,14.82,7.14,22.74,18.08,22.74s17.87-7.72,17.87-22.74v-27.51c0-11.31,9.26-20.49,20.69-20.49Z' />
        <path d='M506.93,95.08h42.6s4.09-21.83-11.95-38.17c-8.77-8.93-19.54-12.89-32.1-12.5-10.43.33-20.45,4.46-28.02,11.58-9.14,8.61-13.61,18.89-13.61,31.06s4.15,21.92,12.65,30.33c8.5,8.22,18.58,12.33,30.64,12.33s21.94-4.11,30.44-12.33c4.04-4,7.06-8.37,9.16-13.12h-21.47c-.19.2-.36.41-.55.6-4.64,4.6-10.49,6.99-17.54,6.99s-12.9-2.4-17.75-6.99c-4.64-4.79-7.06-10.79-7.06-18.18s2.42-13.38,7.06-17.98c4.84-4.79,10.69-7.19,17.75-7.19s12.9,2.4,17.54,7.19c2.45,2.32,4.31,5.8,5.54,8.98h-6.17c-9.48,0-17.16,7.61-17.16,17v.37h-.01Z' />
        <path d='M326.16,95.2h42.6s4.09-21.83-11.95-38.17c-8.77-8.93-19.54-12.89-32.1-12.5-10.43.33-20.45,4.46-28.02,11.58-9.14,8.61-13.6,18.89-13.6,31.06s4.15,21.92,12.65,30.33c8.5,8.22,18.58,12.33,30.64,12.33s21.94-4.11,30.44-12.33c4.04-4,7.06-8.37,9.16-13.11h-21.47c-.19.2-.36.41-.55.6-4.64,4.6-10.49,6.99-17.55,6.99s-12.9-2.4-17.75-6.99c-4.64-4.79-7.06-10.79-7.06-18.18s2.42-13.38,7.06-17.98c4.84-4.79,10.69-7.19,17.75-7.19s12.9,2.4,17.55,7.19c2.45,2.32,4.31,5.8,5.54,8.98h-6.17c-9.48,0-17.17,7.61-17.17,17v.37h0Z' />
        <path d='M454.75,47.33v47.99c0,14.18-3.78,24.63-11.13,31.51-7.56,6.88-16.58,10.43-26.88,10.43s-19.32-3.55-26.88-10.43c-7.56-6.89-11.34-17.53-11.34-31.51v-28.58c0-10.72,8.77-19.42,19.61-19.42v47.99c0,14.82,7.14,22.74,18.08,22.74s17.86-7.72,17.86-22.74v-27.51c0-11.31,9.26-20.49,20.69-20.49Z' />
        <path d='M506.52,95.2h42.6s4.09-21.83-11.95-38.17c-8.77-8.93-19.54-12.89-32.1-12.5-10.43.33-20.45,4.46-28.02,11.58-9.14,8.61-13.6,18.89-13.6,31.06s4.15,21.92,12.65,30.33c8.5,8.22,18.58,12.33,30.64,12.33s21.94-4.11,30.44-12.33c4.04-4,7.06-8.37,9.16-13.11h-21.47c-.19.2-.36.41-.55.6-4.64,4.6-10.49,6.99-17.55,6.99s-12.9-2.4-17.75-6.99c-4.64-4.79-7.06-10.79-7.06-18.18s2.42-13.38,7.06-17.98c4.84-4.79,10.69-7.19,17.75-7.19s12.9,2.4,17.55,7.19c2.45,2.32,4.31,5.8,5.54,8.98h-6.17c-9.48,0-17.17,7.61-17.17,17v.37h-.01Z' />
        <path d='M454.85,150.72v17.79c-.08,20.25-16.12,35.09-36.24,35.69-23.45.71-41.31-15.98-40.77-38.06l19.91.1c.89,9.61,4.77,16.96,13.2,19.33,8.76,2.46,17.15-.39,21.1-7.47,1.36-2.45,1.96-5.23,1.96-8.01v-19.37' />
      </g>
    </svg>
  );
};
